/**
 * The suggestions of every `artifact-suggestions` card on screen (plan 7.4),
 * keyed by the tool call, so the card and the editor's marks read one
 * state: each suggestion is pending, applied, dismissed, stale (the file
 * changed under it) or cannot be applied here.
 *
 * Suggestions name elements of the version they were made on. When any
 * save of that artifact moves the head (the editor's saves, the card's own
 * applies), `notePageSave` maps the pending page targets through the ids the
 * save answered and moves the set to the new version, so Apply keeps working
 * while the user edits. A save from a base the set does not know leaves the
 * set behind; its Apply then meets a 409 and reads as stale.
 */
import { useSyncExternalStore } from 'react';
import type { AgentArtifactKind, ArtifactSuggestion, ArtifactSuggestionsToolResult } from '@tabbio/types';
import { createUuid } from '../editor/editor-ids';
import { isSuggestionApplicable, remapPageTarget } from './suggestions-model';

export type SuggestionStatus = 'pending' | 'applying' | 'applied' | 'dismissed' | 'failed' | 'stale' | 'unsupported';

export type SuggestionItem = ArtifactSuggestion & {
  index: number;
  status: SuggestionStatus;
};

export type SuggestionSet = {
  key: string;
  artifactId: string;
  kind: AgentArtifactKind;
  title: string;
  /** The version the pending targets name now. */
  versionId: string;
  /** One editing session per card, so its applies squash into one version. */
  editSessionId: string;
  items: SuggestionItem[];
};

const sets = new Map<string, SuggestionSet>();
const listeners = new Set<() => void>();
/** Cached pending lists per artifact, so `useSyncExternalStore` sees stable values. */
let pendingCache = new Map<string, SuggestionItemRef[]>();

export type SuggestionItemRef = { key: string; item: SuggestionItem; set: SuggestionSet };

function notify() {
  pendingCache = new Map();
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** A card's result, registered once per tool call (a remount keeps the statuses). */
export function registerSuggestionSet(key: string, result: ArtifactSuggestionsToolResult): SuggestionSet {
  const existing = sets.get(key);
  if (existing) return existing;
  const set: SuggestionSet = {
    key,
    artifactId: result.artifactId,
    kind: result.kind,
    title: result.title,
    versionId: result.versionId,
    editSessionId: createUuid(),
    items: result.suggestions.map((suggestion, index) => ({
      ...suggestion,
      index,
      status: isSuggestionApplicable(suggestion, result.kind) ? 'pending' : 'unsupported',
    })),
  };
  sets.set(key, set);
  notify();
  return set;
}

export function readSuggestionSet(key: string): SuggestionSet | null {
  return sets.get(key) ?? null;
}

export function useSuggestionSet(key: string): SuggestionSet | null {
  return useSyncExternalStore(subscribe, () => sets.get(key) ?? null, () => null);
}

export function setSuggestionStatus(key: string, indexes: readonly number[], status: SuggestionStatus) {
  const set = sets.get(key);
  if (!set) return;
  const wanted = new Set(indexes);
  sets.set(key, {
    ...set,
    items: set.items.map((item) => (wanted.has(item.index) ? { ...item, status } : item)),
  });
  notify();
}

/**
 * A page save of `artifactId` from `baseVersionId` stored `versionId` and
 * renumbered `ids`: sets on that base follow it.
 */
export function notePageSave(
  artifactId: string,
  baseVersionId: string,
  versionId: string,
  ids: Readonly<Record<string, string | null>> | undefined,
) {
  let changed = false;
  for (const [key, set] of sets) {
    if (set.artifactId !== artifactId || set.versionId !== baseVersionId) continue;
    changed = true;
    sets.set(key, {
      ...set,
      versionId,
      items: set.items.map((item) => {
        if (item.status !== 'pending' && item.status !== 'failed') return item;
        const target = remapPageTarget(item.target, ids);
        return target ? { ...item, target } : { ...item, status: 'stale' };
      }),
    });
  }
  if (changed) notify();
}

/** A document save of `artifactId` from `baseVersionId` stored `versionId`. */
export function noteDocumentSave(artifactId: string, baseVersionId: string, versionId: string) {
  let changed = false;
  for (const [key, set] of sets) {
    if (set.artifactId !== artifactId || set.versionId !== baseVersionId) continue;
    changed = true;
    sets.set(key, { ...set, versionId });
  }
  if (changed) notify();
}

/** Pending suggestions of an artifact on a version (the editor's marks), oldest card first. */
export function readPendingSuggestions(artifactId: string, versionId: string | null): SuggestionItemRef[] {
  const cacheKey = `${artifactId}:${versionId ?? ''}`;
  const cached = pendingCache.get(cacheKey);
  if (cached) return cached;
  const out: SuggestionItemRef[] = [];
  for (const [key, set] of sets) {
    if (set.artifactId !== artifactId || (versionId && set.versionId !== versionId)) continue;
    for (const item of set.items) if (item.status === 'pending') out.push({ key, item, set });
  }
  pendingCache.set(cacheKey, out);
  return out;
}

export function usePendingSuggestions(artifactId: string | null, versionId: string | null): SuggestionItemRef[] {
  return useSyncExternalStore(
    subscribe,
    () => (artifactId ? readPendingSuggestions(artifactId, versionId) : EMPTY),
    () => EMPTY,
  );
}

const EMPTY: SuggestionItemRef[] = [];

export function resetSuggestionsForTests() {
  sets.clear();
  pendingCache = new Map();
  listeners.clear();
}
